/**
 * APOLLO'S SUN CHAMBER ☀️
 * 
 * The divine distribution command center where content radiates
 * across all platforms like rays of sunlight.
 * 
 * Features:
 * - Sun Dial Scheduler: Visual circular scheduling interface
 * - Platform Grid: 5 platforms with content previews
 * - Apollo's Prophecy: Engagement predictions
 * - Distribution History: Past distributions
 * - Content Authenticity Engine: AI humanization
 * - Content Remix: Refresh old content
 * - Posting Notifications: Browser & SMS reminders
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWorkflow } from '../../contexts/WorkflowContext';
import { RiverBanner } from '../ui/RiverFlow';
import { 
  Sun, Clock, Send, Calendar, TrendingUp, Eye, Heart, 
  MessageCircle, Share2, Bookmark, Twitter, Linkedin, 
  Facebook, Instagram, FileText, Zap, ChevronRight,
  Copy, Check, Play, Pause, Settings, ExternalLink, Link2, Unlink,
  Bell, BellOff
} from 'lucide-react';
import notificationService from '../../services/NotificationService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Floating sun particles
const SunParticles = () => {
  const particles = useMemo(() => 
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${(i * 17) % 100}%`,
      top: `${(i * 23) % 100}%`,
      size: 4 + (i % 4),
      duration: 8 + (i % 5),
      delay: i * 0.3,
      opacity: 0.3 + (i % 3) * 0.2
    }))
  , []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 animate-pulse"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            opacity: p.opacity,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Platform Icon Component
const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    linkedin: <Linkedin className={className} />,
    twitter: <Twitter className={className} />,
    facebook: <Facebook className={className} />,
    instagram: <Instagram className={className} />,
    medium: <FileText className={className} />
  };
  return icons[platform] || <FileText className={className} />;
};

// Platform Colors
const platformColors = {
  linkedin: { bg: 'from-blue-600 to-blue-700', text: 'text-blue-400', border: 'border-blue-500/30' },
  twitter: { bg: 'from-sky-500 to-sky-600', text: 'text-sky-400', border: 'border-sky-500/30' },
  facebook: { bg: 'from-indigo-600 to-indigo-700', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  instagram: { bg: 'from-pink-500 via-purple-500 to-orange-500', text: 'text-pink-400', border: 'border-pink-500/30' },
  medium: { bg: 'from-gray-700 to-gray-800', text: 'text-gray-300', border: 'border-gray-500/30' }
};

// Sun Dial Scheduler Component
const SunDialScheduler = ({ schedule, onTimeChange }) => {
  const platforms = ['linkedin', 'twitter', 'facebook', 'instagram', 'medium'];
  const [hoveredPlatform, setHoveredPlatform] = useState(null);
  
  // Position platforms around the sun dial
  const getPosition = (index, total) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    const radius = 120;
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius
    };
  };

  return (
    <div className="relative w-full aspect-square max-w-md mx-auto">
      {/* Sun center */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 shadow-lg shadow-orange-500/50 flex items-center justify-center animate-pulse">
          <Sun className="w-12 h-12 text-yellow-100" />
        </div>
        <div className="absolute inset-0 rounded-full bg-yellow-500/20 animate-ping" />
      </div>
      
      {/* Rays connecting to platforms */}
      {platforms.map((platform, i) => {
        const pos = getPosition(i, platforms.length);
        const scheduleData = schedule?.platforms?.[platform];
        const time = scheduleData?.optimal_hour || '10:00 AM';
        
        return (
          <div key={platform} className="absolute" style={{
            top: `calc(50% + ${pos.y}px)`,
            left: `calc(50% + ${pos.x}px)`,
            transform: 'translate(-50%, -50%)'
          }}>
            {/* Ray line */}
            <div 
              className={`absolute w-16 h-0.5 bg-gradient-to-r from-orange-500/80 to-transparent`}
              style={{
                transformOrigin: 'left center',
                transform: `rotate(${Math.atan2(pos.y, pos.x) * 180 / Math.PI + 180}deg)`,
                left: '50%',
                top: '50%'
              }}
            />
            
            {/* Platform node */}
            <div 
              className={`w-16 h-16 rounded-full bg-gradient-to-br ${platformColors[platform].bg} shadow-lg cursor-pointer transition-all duration-300 flex flex-col items-center justify-center ${hoveredPlatform === platform ? 'scale-110' : ''}`}
              onMouseEnter={() => setHoveredPlatform(platform)}
              onMouseLeave={() => setHoveredPlatform(null)}
            >
              <PlatformIcon platform={platform} className="w-6 h-6 text-white" />
              <span className="text-[10px] text-white/80 mt-0.5">{time}</span>
            </div>
            
            {/* Tooltip */}
            {hoveredPlatform === platform && (
              <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm whitespace-nowrap z-10">
                <div className="font-bold text-white capitalize">{platform}</div>
                <div className="text-gray-400">{scheduleData?.optimal_day || 'Tue-Thu'}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Platform Content Preview Card with Smart Copy & Open
const PlatformPreviewCard = ({ platform, content, prophecy, onCopy, platformStatus }) => {
  const [copied, setCopied] = useState(false);
  const colors = platformColors[platform];
  const prediction = prophecy?.predictions?.[platform] || {};
  const isConnected = platformStatus?.[platform]?.connected;
  const canAutoPost = platformStatus?.[platform]?.auto_post_enabled;
  
  // Get full content with hashtags for copying
  const getFullContentWithHashtags = () => {
    let text = '';
    const hashtags = content?.hashtags || [];
    const hashtagText = hashtags.map(t => `#${t.replace(/\s+/g, '')}`).join(' ');
    
    if (platform === 'twitter') {
      // For Twitter, get the full thread
      const thread = content?.thread || [];
      text = thread.join('\n\n---\n\n');
      if (hashtagText) text += '\n\n' + hashtagText;
    } else if (platform === 'instagram') {
      text = content?.caption || content?.first_line || '';
      if (hashtagText) text += '\n\n' + hashtagText;
    } else if (platform === 'medium') {
      // For Medium, include title and content
      text = `# ${content?.headline || 'Article Title'}\n\n${content?.intro_paragraph || content?.content || ''}`;
      if (hashtagText) text += '\n\nTags: ' + hashtags.join(', ');
    } else {
      // LinkedIn, Facebook
      text = content?.post || content?.hook || '';
      if (hashtagText) text += '\n\n' + hashtagText;
    }
    
    return text;
  };
  
  const handleCopy = async () => {
    const textToCopy = getFullContentWithHashtags();
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onCopy) onCopy(platform, textToCopy);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      console.log('Clipboard API not available, using fallback');
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleOpenPlatform = () => {
    const urls = {
      twitter: 'https://twitter.com/intent/tweet',
      linkedin: 'https://www.linkedin.com/feed/',
      facebook: 'https://www.facebook.com/',
      instagram: 'https://www.instagram.com/',
      medium: 'https://medium.com/new-story'
    };
    window.open(urls[platform], '_blank');
  };

  const getPreviewText = () => {
    if (platform === 'twitter') {
      return content?.thread?.[0] || content?.hook || 'Twitter thread preview...';
    }
    if (platform === 'instagram') {
      return content?.first_line || content?.caption?.slice(0, 100) || 'Instagram caption...';
    }
    if (platform === 'medium') {
      return content?.intro_paragraph?.slice(0, 150) || 'Medium article preview...';
    }
    return content?.post?.slice(0, 150) || content?.hook || 'Content preview...';
  };

  return (
    <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border ${colors.border} overflow-hidden hover:border-opacity-60 transition-all`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${colors.bg} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <PlatformIcon platform={platform} className="w-5 h-5 text-white" />
          <span className="font-bold text-white capitalize">{platform}</span>
          {canAutoPost && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${isConnected ? 'bg-green-500/30 text-green-300' : 'bg-gray-500/30 text-gray-300'}`}>
              {isConnected ? '✓ Auto' : 'Connect'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            title="Copy content with hashtags"
          >
            {copied ? <Check className="w-4 h-4 text-green-300" /> : <Copy className="w-4 h-4 text-white" />}
          </button>
          {!canAutoPost && (
            <button
              onClick={handleOpenPlatform}
              className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              title={`Open ${platform}`}
            >
              <ExternalLink className="w-4 h-4 text-white" />
            </button>
          )}
        </div>
      </div>
      
      {/* Content Preview */}
      <div className="p-4">
        <p className="text-gray-300 text-sm line-clamp-3 mb-3">
          {getPreviewText()}
        </p>
        
        {/* Twitter thread indicator */}
        {platform === 'twitter' && content?.thread && (
          <div className="text-xs text-sky-400 mb-3">
            🧵 {content.thread.length} tweets in thread
          </div>
        )}
        
        {/* Instagram carousel indicator */}
        {platform === 'instagram' && content?.carousel_slides && (
          <div className="text-xs text-pink-400 mb-3">
            📱 {content.carousel_slides.length} carousel slides
          </div>
        )}
        
        {/* Hashtags */}
        {content?.hashtags && (
          <div className="flex flex-wrap gap-1 mb-3">
            {content.hashtags.slice(0, 5).map((tag, i) => (
              <span key={i} className={`text-xs ${colors.text} bg-gray-800 px-2 py-0.5 rounded-full`}>
                #{tag}
              </span>
            ))}
            {content.hashtags.length > 5 && (
              <span className="text-xs text-gray-500">+{content.hashtags.length - 5} more</span>
            )}
          </div>
        )}
        
        {/* Prophecy Predictions */}
        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-800">
          <div className="flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-400">
              {prediction.predicted_views || prediction.predicted_impressions || prediction.predicted_reach || '~2.5K'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Heart className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-400">
              {prediction.predicted_likes || prediction.predicted_reactions || prediction.predicted_claps || '~85'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MessageCircle className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-400">
              {prediction.predicted_comments || prediction.predicted_replies || prediction.predicted_responses || '~12'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Share2 className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-400">
              {prediction.predicted_shares || prediction.predicted_retweets || '~8'}
            </span>
          </div>
        </div>
        
        {/* Confidence */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">Confidence</span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r ${colors.bg}`}
                style={{ width: `${prediction.confidence || 75}%` }}
              />
            </div>
            <span className="text-xs text-gray-400">{prediction.confidence || 75}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Content Authenticity Card - Shows AI humanization results
const AuthenticityCard = ({ authenticity, brandVoice }) => {
  if (!authenticity && !brandVoice) return null;
  
  const scoreBefore = authenticity?.overall_score_before || 45;
  const scoreAfter = authenticity?.overall_score_after || 88;
  const improvement = authenticity?.improvement || (scoreAfter - scoreBefore);
  const voiceScore = brandVoice?.overall_score || 80;
  
  return (
    <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 backdrop-blur-sm rounded-xl border border-emerald-500/30 p-6 mb-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <span className="text-2xl">✨</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Content Authenticity Engine</h3>
          <p className="text-sm text-emerald-400/80">AI humanization &amp; brand voice check</p>
        </div>
      </div>
      
      {/* Authenticity Score - Before/After */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-3 bg-gray-900/50 rounded-lg text-center">
          <p className="text-xs text-gray-500 mb-1">Before</p>
          <p className="text-2xl font-bold text-red-400">{scoreBefore}%</p>
          <p className="text-xs text-gray-500">AI-like</p>
        </div>
        <div className="p-3 bg-gray-900/50 rounded-lg text-center">
          <p className="text-xs text-gray-500 mb-1">After</p>
          <p className="text-2xl font-bold text-emerald-400">{scoreAfter}%</p>
          <p className="text-xs text-gray-500">Human-like</p>
        </div>
      </div>
      
      {/* Improvement indicator */}
      <div className="flex items-center justify-center gap-2 mb-4 p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
        <TrendingUp className="w-5 h-5 text-emerald-400" />
        <span className="text-emerald-400 font-bold">+{improvement}% improvement</span>
      </div>
      
      {/* Brand Voice Alignment */}
      {brandVoice && (
        <div className="p-3 bg-gray-900/50 rounded-lg mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Brand Voice Alignment</span>
            <span className={`text-lg font-bold ${voiceScore >= 80 ? 'text-emerald-400' : voiceScore >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
              {voiceScore}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${voiceScore >= 80 ? 'bg-emerald-500' : voiceScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
              style={{ width: `${voiceScore}%` }}
            />
          </div>
          {brandVoice.message && (
            <p className="text-xs text-gray-500 mt-2">{brandVoice.message}</p>
          )}
        </div>
      )}
      
      {/* Techniques Applied */}
      {authenticity?.techniques_applied && authenticity.techniques_applied.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 mb-2">Humanization techniques applied:</p>
          <div className="flex flex-wrap gap-1">
            {authenticity.techniques_applied.slice(0, 5).map((tech, i) => (
              <span key={i} className="px-2 py-0.5 text-xs bg-gray-800 text-gray-400 rounded">
                {tech.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Platform-specific scores */}
      {authenticity?.platform_scores && (
        <div className="mt-4 pt-4 border-t border-gray-800">
          <p className="text-xs text-gray-500 mb-2">Platform scores:</p>
          <div className="space-y-2">
            {Object.entries(authenticity.platform_scores).slice(0, 3).map(([platform, scores]) => (
              <div key={platform} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <PlatformIcon platform={platform} className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-400 capitalize">{platform}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-400 text-xs">{scores.before}%</span>
                  <span className="text-gray-600">→</span>
                  <span className="text-emerald-400 text-xs font-medium">{scores.after}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Apollo's Prophecy Card
const ProphecyCard = ({ prophecy }) => {
  return (
    <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 backdrop-blur-sm rounded-xl border border-yellow-500/30 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
          <span className="text-2xl">🔮</span>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Apollo&apos;s Prophecy</h3>
          <p className="text-sm text-yellow-400/80">Divine engagement predictions</p>
        </div>
      </div>
      
      {/* Overall score */}
      <div className="flex items-center justify-between mb-4 p-3 bg-gray-900/50 rounded-lg">
        <span className="text-gray-400">Prophecy Score</span>
        <div className="flex items-center gap-2">
          <div className="text-3xl font-bold text-yellow-400">{prophecy?.overall_score || 75}</div>
          <span className="text-gray-500">/100</span>
        </div>
      </div>
      
      {/* Estimated reach */}
      <div className="flex items-center justify-between mb-4 p-3 bg-gray-900/50 rounded-lg">
        <span className="text-gray-400">Total Estimated Reach</span>
        <span className="text-xl font-bold text-orange-400">
          {(prophecy?.total_estimated_reach || 12800).toLocaleString()}
        </span>
      </div>
      
      {/* Best platform */}
      <div className="flex items-center justify-between mb-4 p-3 bg-gray-900/50 rounded-lg">
        <span className="text-gray-400">Best Platform</span>
        <div className="flex items-center gap-2">
          <PlatformIcon platform={prophecy?.best_performing_platform || 'linkedin'} className="w-5 h-5 text-blue-400" />
          <span className="text-white font-medium capitalize">{prophecy?.best_performing_platform || 'LinkedIn'}</span>
        </div>
      </div>
      
      {/* Apollo's message */}
      <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg border border-yellow-500/20">
        <p className="text-sm text-yellow-200 italic">
          &ldquo;{prophecy?.apollo_says || 'The sun shines brightest on LinkedIn! Focus your energy there for maximum impact.'}&rdquo;
        </p>
      </div>
    </div>
  );
};

// Distribution History Item
const DistributionHistoryItem = ({ distribution }) => {
  const date = new Date(distribution.created_at).toLocaleDateString();
  const platforms = Object.keys(distribution.platforms || {});
  
  return (
    <div className="bg-gray-900/60 backdrop-blur-sm rounded-lg border border-gray-800 p-4 hover:border-yellow-500/30 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-white truncate flex-1">{distribution.source_content_title}</h4>
        <span className="text-xs text-gray-500 ml-2">{date}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex -space-x-2">
          {platforms.slice(0, 5).map(p => (
            <div key={p} className={`w-6 h-6 rounded-full bg-gradient-to-br ${platformColors[p]?.bg || 'from-gray-600 to-gray-700'} flex items-center justify-center border-2 border-gray-900`}>
              <PlatformIcon platform={p} className="w-3 h-3 text-white" />
            </div>
          ))}
        </div>
        <span className="text-xs text-gray-400">{platforms.length} platforms</span>
        <div className="flex items-center gap-1 ml-auto">
          <Eye className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs text-gray-400">{distribution.estimated_reach?.toLocaleString() || '~5K'}</span>
        </div>
      </div>
    </div>
  );
};

// Content Remix Modal
const ContentRemixModal = ({ API, show, onClose, contentToRemix, onRemixComplete }) => {
  const [remixStyle, setRemixStyle] = useState('fresh_angle');
  const [remixing, setRemixing] = useState(false);
  const [remixResult, setRemixResult] = useState(null);
  
  const remixStyles = [
    { id: 'fresh_angle', name: 'Fresh Angle', emoji: '🔄', desc: 'Same topic, completely different perspective' },
    { id: 'different_audience', name: 'Different Audience', emoji: '👥', desc: 'Adapt for a new target segment' },
    { id: 'platform_optimize', name: 'Platform Optimize', emoji: '📱', desc: 'Re-optimize for better platform fit' },
    { id: 'trend_update', name: 'Trend Update', emoji: '📈', desc: 'Update with current trends & data' },
  ];
  
  const handleRemix = async () => {
    if (!contentToRemix) return;
    
    setRemixing(true);
    setRemixResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      const response = await fetch(`${apiUrl}/agents/remix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          original_content: contentToRemix,
          remix_style: remixStyle,
          target_platforms: ['linkedin', 'twitter'],
          preserve_core_message: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setRemixResult(data.remix);
        if (onRemixComplete) onRemixComplete(data);
        
        // Send notification when remix is complete
        notificationService.sendRemixComplete(remixStyle);
      } else {
        const error = await response.json();
        alert(error.detail || 'Remix failed');
      }
    } catch (err) {
      console.error('Remix error:', err);
      alert('Failed to remix content');
    } finally {
      setRemixing(false);
    }
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden border border-yellow-500/30">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <span className="text-2xl">🔄</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Content Remix</h2>
              <p className="text-sm text-gray-400">Refresh your content with a new angle</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {!remixResult ? (
            <>
              {/* Remix Style Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">Choose Remix Style</label>
                <div className="grid grid-cols-2 gap-3">
                  {remixStyles.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setRemixStyle(style.id)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        remixStyle === style.id
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{style.emoji}</span>
                        <span className="font-medium text-white">{style.name}</span>
                      </div>
                      <p className="text-xs text-gray-400">{style.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Content Preview */}
              {contentToRemix && (
                <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700 mb-6">
                  <p className="text-xs text-gray-500 mb-1">Content to Remix</p>
                  <p className="text-white font-medium">
                    {contentToRemix.blog_post?.title || contentToRemix.topic || 'Selected Content'}
                  </p>
                </div>
              )}
              
              {/* Credits Info */}
              <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20 text-center">
                <span className="text-yellow-400 text-sm">⚡ 10 credits per remix</span>
              </div>
            </>
          ) : (
            /* Remix Results */
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-white">Remix Complete! 🎉</h3>
                  <p className="text-sm text-gray-400">Freshness Score: {remixResult.freshness_score}%</p>
                </div>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                  {remixResult.remix_style}
                </span>
              </div>
              
              {/* New Angle */}
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
                <p className="text-xs text-purple-400 mb-1">New Angle</p>
                <p className="text-white">{remixResult.remixed_content?.new_angle}</p>
              </div>
              
              {/* New Hook */}
              <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                <p className="text-xs text-gray-500 mb-1">New Hook</p>
                <p className="text-white font-medium">{remixResult.remixed_content?.new_hook}</p>
              </div>
              
              {/* LinkedIn Preview */}
              {remixResult.remixed_content?.linkedin && (
                <div className="p-4 bg-blue-900/20 rounded-xl border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Linkedin className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-blue-400">LinkedIn Version</span>
                  </div>
                  <p className="text-gray-300 text-sm whitespace-pre-wrap">
                    {remixResult.remixed_content.linkedin.post?.slice(0, 300)}...
                  </p>
                </div>
              )}
              
              {/* Apollo Says */}
              {remixResult.apollo_says && (
                <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <p className="text-sm text-yellow-200 italic">☀️ {remixResult.apollo_says}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white">
            {remixResult ? 'Close' : 'Cancel'}
          </button>
          {!remixResult && (
            <button
              onClick={handleRemix}
              disabled={remixing || !contentToRemix}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg font-medium text-white hover:from-purple-400 hover:to-pink-500 disabled:opacity-50 flex items-center gap-2"
            >
              {remixing ? (
                <>
                  <Sun className="w-4 h-4 animate-spin" />
                  Remixing...
                </>
              ) : (
                <>
                  🔄 Remix Content
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Notification Settings Panel
const NotificationPanel = ({ postingSchedule, contentTitle, onScheduleReminders }) => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [permission, setPermission] = useState(notificationService.getPermission());
  const [scheduledReminders, setScheduledReminders] = useState([]);

  const handleEnableNotifications = async () => {
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      // Schedule reminders if we have a posting schedule
      if (postingSchedule && contentTitle) {
        const reminders = notificationService.schedulePostingReminders(postingSchedule, contentTitle);
        setScheduledReminders(reminders);
        if (onScheduleReminders) onScheduleReminders(reminders);
      }
      // Send a test notification
      notificationService.send('🔔 Notifications Enabled!', {
        body: 'You\'ll receive reminders when it\'s optimal posting time.',
        tag: 'notifications-enabled'
      });
    } else if (permission === 'default') {
      const granted = await notificationService.requestPermission();
      setPermission(notificationService.getPermission());
      if (granted) {
        setNotificationsEnabled(true);
        notificationService.send('🔔 Notifications Enabled!', {
          body: 'You\'ll receive reminders when it\'s optimal posting time.',
          tag: 'notifications-enabled'
        });
      }
    } else {
      alert('Notifications are blocked. Please enable them in your browser settings.');
    }
  };

  const handleDisableNotifications = () => {
    setNotificationsEnabled(false);
    // Cancel scheduled reminders
    notificationService.cancelAllScheduled(scheduledReminders);
    setScheduledReminders([]);
  };

  const handleTestNotification = () => {
    notificationService.sendPostingReminder('LinkedIn', contentTitle || 'Your Content');
  };

  if (!notificationService.isSupported()) {
    return null; // Don't show if not supported
  }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl border border-purple-500/20 p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            notificationsEnabled 
              ? 'bg-gradient-to-br from-purple-500 to-pink-600' 
              : 'bg-gray-700'
          }`}>
            {notificationsEnabled ? (
              <Bell className="w-5 h-5 text-white" />
            ) : (
              <BellOff className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="font-medium text-white">Posting Reminders</h3>
            <p className="text-xs text-gray-400">
              {notificationsEnabled 
                ? `${scheduledReminders.length} reminders scheduled` 
                : 'Get notified when it\'s time to post'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {notificationsEnabled && (
            <button
              onClick={handleTestNotification}
              className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300"
            >
              Test
            </button>
          )}
          <button
            onClick={notificationsEnabled ? handleDisableNotifications : handleEnableNotifications}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              notificationsEnabled
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-400 hover:to-pink-500'
            }`}
          >
            {notificationsEnabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
      
      {/* Scheduled reminders list */}
      {notificationsEnabled && scheduledReminders.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-800">
          <p className="text-xs text-gray-500 mb-2">Upcoming reminders:</p>
          <div className="flex flex-wrap gap-2">
            {scheduledReminders.map(({ platform, time }) => (
              <span 
                key={platform} 
                className="px-2 py-1 text-xs bg-purple-500/10 text-purple-400 rounded border border-purple-500/20"
              >
                {platform}: {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {permission === 'denied' && (
        <p className="mt-2 text-xs text-red-400">
          ⚠️ Notifications blocked. Enable in browser settings.
        </p>
      )}
    </div>
  );
};

// Platform Connection Panel
const PlatformConnectionPanel = ({ API, platformStatus, onRefresh }) => {
  const [showMediumModal, setShowMediumModal] = useState(false);
  const [mediumToken, setMediumToken] = useState('');
  const [connecting, setConnecting] = useState(null);

  const handleLinkedInConnect = async () => {
    setConnecting('linkedin');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      const response = await fetch(`${apiUrl}/platforms/linkedin/connect`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authorization_url) {
          window.location.href = data.authorization_url;
        }
      } else {
        alert('LinkedIn integration not configured. Contact support.');
      }
    } catch (err) {
      console.error('LinkedIn connect error:', err);
      alert('Failed to connect LinkedIn');
    } finally {
      setConnecting(null);
    }
  };

  const handleMediumConnect = async () => {
    if (!mediumToken.trim()) {
      alert('Please enter your Medium integration token');
      return;
    }
    
    setConnecting('medium');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      const response = await fetch(`${apiUrl}/platforms/medium/connect`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ integration_token: mediumToken })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Connected to Medium as @${data.username}!`);
        setShowMediumModal(false);
        setMediumToken('');
        if (onRefresh) onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Invalid Medium token');
      }
    } catch (err) {
      console.error('Medium connect error:', err);
      alert('Failed to connect Medium');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      await fetch(`${apiUrl}/platforms/${platform}/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Disconnect error:', err);
    }
  };

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'from-blue-600 to-blue-700', autoPost: true },
    { id: 'medium', name: 'Medium', icon: FileText, color: 'from-gray-700 to-gray-800', autoPost: true },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'from-sky-500 to-sky-600', autoPost: false },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'from-indigo-600 to-indigo-700', autoPost: false },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'from-pink-500 to-purple-600', autoPost: false },
  ];

  return (
    <>
      <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl border border-yellow-500/20 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-yellow-400" />
            Platform Connections
          </h3>
          <span className="text-xs text-gray-500">Auto-post to connected platforms</span>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {platforms.map(({ id, name, icon: Icon, color, autoPost }) => {
            const status = platformStatus?.[id];
            const isConnected = status?.connected;
            
            return (
              <div
                key={id}
                className={`relative rounded-lg p-3 border transition-all ${
                  isConnected 
                    ? 'border-green-500/50 bg-green-500/10' 
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-300">{name}</span>
                  
                  {autoPost ? (
                    isConnected ? (
                      <button
                        onClick={() => handleDisconnect(id)}
                        className="text-xs text-green-400 hover:text-red-400 flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" /> Connected
                      </button>
                    ) : (
                      <button
                        onClick={() => id === 'linkedin' ? handleLinkedInConnect() : setShowMediumModal(true)}
                        disabled={connecting === id}
                        className="text-xs text-yellow-400 hover:text-yellow-300"
                      >
                        {connecting === id ? 'Connecting...' : 'Connect'}
                      </button>
                    )
                  ) : (
                    <span className="text-xs text-gray-500">Copy & Paste</span>
                  )}
                </div>
                
                {!autoPost && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gray-700 flex items-center justify-center">
                    <Copy className="w-2.5 h-2.5 text-gray-400" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <p className="text-xs text-gray-500 mt-3 text-center">
          LinkedIn & Medium support auto-posting. Twitter, Facebook, Instagram use copy & open (API limitations).
        </p>
      </div>
      
      {/* Medium Token Modal */}
      {showMediumModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-md w-full border border-gray-700 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Connect Medium</h3>
            <p className="text-sm text-gray-400 mb-4">
              Enter your Medium integration token. You can find this in your Medium Settings &gt; Security and apps &gt; Integration tokens.
            </p>
            <input
              type="text"
              value={mediumToken}
              onChange={(e) => setMediumToken(e.target.value)}
              placeholder="Your Medium integration token"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowMediumModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleMediumConnect}
                disabled={connecting === 'medium'}
                className="px-6 py-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg font-medium text-white hover:from-gray-600 hover:to-gray-700 disabled:opacity-50"
              >
                {connecting === 'medium' ? 'Connecting...' : 'Connect Medium'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Main Apollo Sun Chamber Component
const ApolloSunChamber = ({ API, user, onBack, onRunAgent }) => {
  const { workflow } = useWorkflow();
  const [distributions, setDistributions] = useState([]);
  const [latestDistribution, setLatestDistribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [hermesPackages, setHermesPackages] = useState([]);
  const [showContentSelector, setShowContentSelector] = useState(false);
  const [runProgress, setRunProgress] = useState(null);
  const [platformStatus, setPlatformStatus] = useState({});
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [contentToRemix, setContentToRemix] = useState(null);

  // Fetch distribution history and Hermes packages
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      // Fetch all outputs
      const response = await fetch(`${apiUrl}/agents/outputs/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const outputs = data.outputs || [];
        
        // Filter Apollo distributions
        const apolloOutputs = outputs.filter(o => 
          o.agent_type === 'distribution_machine' || 
          o.output_data?.platforms !== undefined
        );
        
        setDistributions(apolloOutputs);
        if (apolloOutputs.length > 0) {
          setLatestDistribution(apolloOutputs[0].output_data);
        }
        
        // Filter Hermes packages for content selection
        const hermesOutputs = outputs.filter(o =>
          o.agent_type === 'content_engine' ||
          o.output_data?.blog_post !== undefined
        );
        setHermesPackages(hermesOutputs);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [API]);

  // Fetch platform connection status
  const fetchPlatformStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      const response = await fetch(`${apiUrl}/platforms/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlatformStatus(data.platforms || {});
      }
    } catch (err) {
      console.error('Error fetching platform status:', err);
    }
  }, [API]);

  useEffect(() => {
    fetchData();
    fetchPlatformStatus();
  }, [fetchData, fetchPlatformStatus]);

  // Run Apollo distribution
  const handleDistribute = async () => {
    if (!selectedContent && hermesPackages.length === 0) {
      setShowContentSelector(true);
      return;
    }
    
    setRunning(true);
    setRunProgress({ step: 0, message: 'Initiating distribution...' });
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API || `${API_URL}/api`;
      
      const sourceContent = selectedContent?.output_data || hermesPackages[0]?.output_data;
      
      // Show progress steps - Updated for new 10-step workflow
      const progressSteps = [
        'Analyzing source content...',
        'Checking brand voice alignment...',
        'Planning platform strategy...',
        'Crafting LinkedIn version...',
        'Creating Twitter thread...',
        'Adapting for Facebook...',
        'Designing Instagram caption...',
        'Preparing Medium article...',
        'Authenticity Pass - Humanizing content...',
        'Generating Apollo\'s Prophecy...'
      ];
      
      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        if (stepIndex < progressSteps.length) {
          setRunProgress({ step: stepIndex, message: progressSteps[stepIndex] });
          stepIndex++;
        }
      }, 2500);
      
      const response = await fetch(`${apiUrl}/agents/quick-run/distribution_machine`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_content: sourceContent,
          distribution_config: {
            platforms: ['linkedin', 'twitter', 'facebook', 'instagram', 'medium'],
            timing_mode: 'optimal'
          }
        })
      });
      
      clearInterval(progressInterval);
      const result = await response.json();
      
      if (result.success) {
        setLatestDistribution(result.distribution);
        setRunProgress({ step: 8, message: '☀️ Distribution complete!' });
        fetchData(); // Refresh history
        
        // Send browser notification
        notificationService.sendDistributionComplete(5);
        
        setTimeout(() => setRunProgress(null), 2000);
      } else {
        setRunProgress(null);
        alert('Distribution failed: ' + (result.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('Distribution error:', err);
      setRunProgress(null);
      alert('Failed to distribute: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  // Select content from Hermes packages
  const handleSelectContent = (pkg) => {
    setSelectedContent(pkg);
    setShowContentSelector(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-yellow-950/10 to-gray-950 text-white relative overflow-x-hidden">
      <SunParticles />
      
      {/* River Banner */}
      {workflow.active && workflow.targetTool === 'apollo-chamber' && (
        <div className="relative z-10 px-6 pt-4"><RiverBanner /></div>
      )}
      
      {/* Content Selector Modal */}
      {showContentSelector && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-yellow-500/30">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Select Content to Distribute</h2>
                <p className="text-sm text-gray-400">Choose a Hermes content package</p>
              </div>
              <button onClick={() => setShowContentSelector(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {hermesPackages.length === 0 ? (
                <div className="text-center py-8">
                  <Sun className="w-12 h-12 text-yellow-500/50 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No content packages found</p>
                  <p className="text-sm text-gray-500">Run Hermes first to create content that Apollo can distribute</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {hermesPackages.map((pkg, i) => {
                    const data = pkg.output_data || {};
                    const title = data.blog_post?.title || data.topic || 'Untitled Package';
                    const date = new Date(pkg.created_at).toLocaleDateString();
                    const isSelected = selectedContent?.id === pkg.id;
                    
                    return (
                      <div
                        key={pkg.id || i}
                        onClick={() => handleSelectContent(pkg)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-yellow-500 bg-yellow-500/10' 
                            : 'border-gray-700 hover:border-yellow-500/50 bg-gray-800/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-white mb-1">{title}</h3>
                            <p className="text-sm text-gray-400 line-clamp-2">
                              {data.blog_post?.meta_description || data.content_brief?.key_message || 'Content ready for distribution'}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                              <span>📅 {date}</span>
                              {data.blog_post?.word_count && <span>📝 {data.blog_post.word_count} words</span>}
                            </div>
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center">
                              <Check className="w-4 h-4 text-gray-900" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {hermesPackages.length > 0 && (
              <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => setShowContentSelector(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowContentSelector(false);
                    if (selectedContent || hermesPackages.length > 0) {
                      handleDistribute();
                    }
                  }}
                  disabled={!selectedContent && hermesPackages.length === 0}
                  className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg font-medium text-white hover:from-yellow-400 hover:to-orange-500 disabled:opacity-50"
                >
                  ☀️ Distribute Selected
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Content Remix Modal */}
      <ContentRemixModal
        API={API}
        show={showRemixModal}
        onClose={() => {
          setShowRemixModal(false);
          setContentToRemix(null);
        }}
        contentToRemix={contentToRemix}
        onRemixComplete={(data) => {
          fetchData(); // Refresh after remix
        }}
      />
      
      {/* Distribution Progress Overlay */}
      {runProgress && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-md w-full mx-4 border border-yellow-500/30 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/50 animate-pulse">
              <Sun className="w-10 h-10 text-yellow-100 animate-spin" style={{ animationDuration: '3s' }} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Apollo is Working</h3>
            <p className="text-yellow-400 mb-4">{runProgress.message}</p>
            <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500"
                style={{ width: `${((runProgress.step + 1) / 10) * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">Step {runProgress.step + 1} of 10</p>
          </div>
        </div>
      )}
      
      {/* Gradient overlays */}
      <div className="hidden md:block absolute top-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-0 left-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      
      {/* Header */}
      <div className="relative z-10 border-b border-yellow-500/20 bg-gray-900/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                  <Sun className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-100" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center text-xs border-2 border-gray-900">
                  ☀️
                </div>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                  Apollo&apos;s Sun Chamber
                </h1>
                <p className="text-xs sm:text-sm text-gray-400">Divine Distribution Command Center</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="px-3 sm:px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-yellow-500/30 transition-colors text-gray-400 hover:text-white text-sm"
                >
                  ← Back
                </button>
              )}
              <button
                onClick={() => setShowContentSelector(true)}
                className="px-3 sm:px-4 py-2 rounded-lg bg-gray-800/50 border border-gray-700/50 hover:border-yellow-500/30 transition-colors text-gray-300 hover:text-white text-sm flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {selectedContent ? 'Change Content' : 'Select Content'}
                </span>
              </button>
              <button
                onClick={() => {
                  const content = selectedContent?.output_data || hermesPackages[0]?.output_data;
                  if (content) {
                    setContentToRemix(content);
                    setShowRemixModal(true);
                  } else {
                    setShowContentSelector(true);
                  }
                }}
                className="px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 hover:border-purple-400/50 transition-colors text-purple-300 hover:text-white text-sm flex items-center gap-2"
              >
                <span>🔄</span>
                <span className="hidden sm:inline">Remix</span>
              </button>
              <button
                onClick={handleDistribute}
                disabled={running}
                className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-bold text-sm sm:text-base bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 shadow-lg shadow-orange-500/25 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {running ? (
                  <>
                    <Sun className="w-5 h-5 animate-spin" />
                    <span>Distributing...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    <span>Distribute Content</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Selected Content Banner */}
          {(selectedContent || hermesPackages.length > 0) && (
            <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-yellow-500/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                  👟
                </div>
                <div>
                  <p className="text-xs text-gray-400">Content to Distribute</p>
                  <p className="text-sm font-medium text-white truncate max-w-xs sm:max-w-md">
                    {(selectedContent?.output_data || hermesPackages[0]?.output_data)?.blog_post?.title || 
                     (selectedContent?.output_data || hermesPackages[0]?.output_data)?.topic || 
                     'Latest Hermes Package'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowContentSelector(true)}
                className="text-xs text-yellow-400 hover:text-yellow-300"
              >
                Change
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        
        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Sun className="w-12 h-12 text-yellow-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Notification Settings Panel */}
            <NotificationPanel 
              postingSchedule={latestDistribution?.posting_schedule}
              contentTitle={
                (selectedContent?.output_data || hermesPackages[0]?.output_data)?.blog_post?.title ||
                (selectedContent?.output_data || hermesPackages[0]?.output_data)?.topic ||
                'Your Content'
              }
            />
            
            {/* Platform Connections Panel */}
            <div className="mb-6">
              <PlatformConnectionPanel 
                API={API} 
                platformStatus={platformStatus} 
                onRefresh={fetchPlatformStatus} 
              />
            </div>
            
            {/* Top Section: Sun Dial + Prophecy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Sun Dial Scheduler */}
              <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl border border-yellow-500/20 p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Sun Dial Scheduler
                </h3>
                <SunDialScheduler schedule={latestDistribution?.posting_schedule} />
                <p className="text-center text-sm text-gray-500 mt-4">
                  Content radiates at optimal times for each platform
                </p>
              </div>
              
              {/* Content Authenticity Engine Results */}
              {latestDistribution && (latestDistribution.authenticity || latestDistribution.brand_voice_alignment) && (
                <AuthenticityCard 
                  authenticity={latestDistribution.authenticity} 
                  brandVoice={latestDistribution.brand_voice_alignment}
                />
              )}
              
              {/* Apollo's Prophecy */}
              <ProphecyCard prophecy={latestDistribution?.prophecy} />
            </div>
            
            {/* Platform Content Grid */}
            {latestDistribution && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-orange-400" />
                  Platform Content Preview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(latestDistribution.platforms || {}).map(([platform, content]) => (
                    <PlatformPreviewCard
                      key={platform}
                      platform={platform}
                      content={content}
                      prophecy={latestDistribution.prophecy}
                      platformStatus={platformStatus}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* No Distribution State */}
            {!latestDistribution && (
              <div className="text-center py-16">
                <Sun className="w-16 h-16 text-yellow-500/50 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Distributions Yet</h3>
                <p className="text-gray-400 mb-6">Run Apollo to spread your content across all platforms</p>
                <button
                  onClick={handleDistribute}
                  disabled={running || hermesPackages.length === 0}
                  className="px-6 py-3 rounded-xl font-bold bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-400 hover:to-orange-500 shadow-lg shadow-orange-500/25 transition-all duration-300 hover:scale-105 disabled:opacity-50"
                >
                  {hermesPackages.length === 0 ? 'Run Hermes First' : '☀️ Start Distribution'}
                </button>
              </div>
            )}
            
            {/* Distribution History */}
            {distributions.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-yellow-400" />
                  Distribution History
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {distributions.slice(0, 6).map((dist, i) => (
                    <DistributionHistoryItem key={i} distribution={dist.output_data} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApolloSunChamber;
